import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Badge } from '../../../components/ui/Badge';
import { copyToClipboard, STAGE_LABELS } from '../utils/linkTokenFormat';

/**
 * Shown after a successful regenerate. Displays the freshly generated link with
 * a copy button (mirroring InviteSummaryModal's copy-link block) and whether the
 * email was sent. No existing shared component surfaces a link + copy + email
 * status together, so this small modal is purpose-built but reuses the existing
 * modal shell / Button / Badge styling.
 */
const RegenerateResultModal = ({ result, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  const link = result.link;
  const handleCopy = () => {
    copyToClipboard(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold text-slate-900">Link Regenerated</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-6">
            {/* Stage + email status */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                {STAGE_LABELS[result.stage] || result.stage}
              </Badge>
              {result.email_sent ? (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                  Email sent
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                  Email not sent
                </Badge>
              )}
            </div>

            {!result.email_sent && result.email_error && (
              <p className="text-sm text-amber-700">
                The email could not be delivered ({result.email_error}). Share the link below
                manually.
              </p>
            )}

            {/* New link */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
                New Link
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 break-all">
                  {link}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  iconName={copied ? 'Check' : 'Copy'}
                  iconPosition="left"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegenerateResultModal;
